import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles,
  X,
  Send,
  HelpCircle,
  Zap,
  Gauge,
  Brain,
  FolderOpen,
  type LucideIcon,
} from 'lucide-react';
import {
  appAssistantApi,
  type AppAssistantCapability,
  type AppAssistantMessage,
  type AppAssistantPatientRef,
  type AppAssistantStatus,
  type AssistantEffort,
} from '@/api/app-assistant.api';
import { ApiError } from '@/api/client';
import { useSession } from '@/app/session';
import { AssistantProposalCard } from './AssistantProposalCard';
import { AssistantMessage } from './AssistantMessage';
import { PatientPeek } from './PatientPeek';
import './AssistantPanel.css';
import { useTranslation } from 'react-i18next';

interface Conversation {
  threadId: string | null;
  messages: AppAssistantMessage[];
}

const EFFORTS: AssistantEffort[] = ['low', 'mid', 'high'];

/**
 * The virtual assistant, on its own — reached from the animated rail icon, not
 * buried in the crowded command centre. A roomy panel with space for a real
 * conversation and a comfortable input, so the feature reads as a first-class
 * tool rather than a cramped afterthought.
 *
 * It shows only to staff who hold `asistent.foloseste`. A clinic without AI
 * still gets it, answering from the application's help (`help`), with no
 * model and none of the clinic's data.
 * Which model answers is never named — the assistant is the assistant.
 */
export function AssistantPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation('common');
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['app-assistant', i18n.language],
    queryFn: () => appAssistantApi.status(i18n.language),
  });
  const { can } = useSession();
  const canSeeRecord = can('fisa.vede') || can('imagistica.vede');
  const [own, setOwn] = useState<Conversation | null>(null);
  const [draft, setDraft] = useState('');
  const [effort, setEffort] = useState<AssistantEffort>('mid');
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [peek, setPeek] = useState<AppAssistantPatientRef | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const last = status.data?.threads[0];
  const shown: Conversation = own ?? {
    threadId: last?.id ?? null,
    messages: last?.messages ?? [],
  };
  const { threadId, messages } = shown;

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const ask = useMutation({
    mutationFn: (question: string) =>
      appAssistantApi.ask({
        question,
        ...(threadId ? { threadId } : {}),
        effort,
        language: i18n.language,
      }),
    onMutate: (question) => {
      setError(null);
      setOwn({
        threadId,
        messages: [
          ...messages,
          {
            id: null,
            role: 'user',
            content: question,
            demo: false,
            createdAt: new Date().toISOString(),
            proposals: [],
          },
        ],
      });
      setDraft('');
    },
    onSuccess: (res) => {
      setOwn((c) => ({
        threadId: res.threadId ?? c?.threadId ?? null,
        messages: [...(c?.messages ?? []), res.answer],
      }));
      if (res.mode === 'live') {
        qc.setQueryData<AppAssistantStatus>(['app-assistant', i18n.language], (old) =>
          old ? { ...old, limit: { ...old.limit, usedToday: old.limit.usedToday + 1 } } : old,
        );
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('asistentulNuAPutut'));
    },
  });

  const send = (text: string) => {
    const q = text.trim();
    if (q.length < 2 || ask.isPending) return;
    ask.mutate(q);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(draft);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  const mode = status.data?.mode ?? 'off';
  const reason = status.data?.reason ?? null;
  const suggestions = status.data?.suggestions ?? [];
  const reads = status.data?.reads ?? [];
  const proposes = status.data?.proposes ?? [];
  const capabilities = status.data?.capabilities ?? [];
  const limit = status.data?.limit ?? { perDay: null, usedToday: 0 };

  const effortLabel: Record<AssistantEffort, string> = {
    low: t('efortRapid'),
    mid: t('efortEchilibrat'),
    high: t('efortProfund'),
  };
  const effortIcon: Record<AssistantEffort, LucideIcon> = { low: Zap, mid: Gauge, high: Brain };
  const effortHint: Record<AssistantEffort, string> = {
    low: t('efortRapidHint'),
    mid: t('efortEchilibratHint'),
    high: t('efortProfundHint'),
  };

  return (
    <aside className="asp" role="dialog" aria-label={t('asistentVirtual')}>
      <header className="asp__head">
        <span className="asp__title">
          <span className="asp__spark" aria-hidden>
            <Sparkles size={15} />
          </span>
          {t('asistentVirtual')}
          {mode === 'demo' ? <span className="asp__badge">{t('demonstrativBadge')}</span> : null}
        </span>
        <div className="asp__headacts">
          {messages.length > 0 && mode !== 'off' ? (
            <button
              type="button"
              className="asp__new"
              onClick={() => {
                setOwn({ threadId: null, messages: [] });
                setError(null);
              }}
            >
              {t('conversatieNoua')}
            </button>
          ) : null}
          {mode !== 'off' && capabilities.length > 0 ? (
            <button
              type="button"
              className="asp__help"
              onClick={() => setHelpOpen(true)}
              aria-label={t('ceStieAsistentul')}
              title={t('ceStieAsistentul')}
            >
              <HelpCircle size={16} />
            </button>
          ) : null}
          <button type="button" className="asp__close" onClick={onClose} aria-label={t('inchide')}>
            <X size={16} />
          </button>
        </div>
      </header>

      {helpOpen ? (
        <AssistantCapabilities
          capabilities={capabilities}
          onClose={() => setHelpOpen(false)}
          onPick={(q) => {
            setHelpOpen(false);
            send(q);
          }}
        />
      ) : null}

      {mode === 'off' ? (
        <div className="asp__off">
          <span className="asp__officon" aria-hidden>
            <Sparkles size={22} />
          </span>
          <p className="asp__offtitle">{t('asistentulNuEsteDisponibil')}</p>
          <p className="asp__offreason">{reason ?? t('asistentulNuEsteDisponibil')}</p>
        </div>
      ) : (
        <>
          {mode === 'live' ? (
            <div className="asp__effortwrap">
              <div className="asp__effort" role="group" aria-label={t('efortAsistent')}>
                {EFFORTS.map((e) => {
                  const Icon = effortIcon[e];
                  return (
                    <button
                      key={e}
                      type="button"
                      className={`asp__effortbtn ${effort === e ? 'is-on' : ''}`}
                      aria-pressed={effort === e}
                      onClick={() => setEffort(e)}
                      title={`${effortLabel[e]} — ${effortHint[e]}`}
                    >
                      <Icon size={15} aria-hidden="true" />
                      <span>{effortLabel[e]}</span>
                    </button>
                  );
                })}
              </div>
              <p className="asp__efforthint">
                {t('efortExplica')} <strong>{effortHint[effort]}</strong>
              </p>
            </div>
          ) : null}

          <div className="asp__body" ref={listRef}>
            <p className="asp__reads">
              {reads.length
                ? t('asistentCitesteReads', { reads: reads.join(', ') })
                : t('raspundeDespreFolosireaAplicatiei')}
              {proposes.length
                ? ` ${t('asistentPregatesteProposes', { proposes: proposes.join(', ') })}`
                : ''}
            </p>

            {messages.length > 0 ? (
              <div className="asp__list">
                {messages.map((m, i) => (
                  <div key={m.id ?? `${i}-${m.createdAt}`} className="asp__turn">
                    <div className={`asp__msg asp__msg--${m.role}`}>
                      <AssistantMessage content={m.content} role={m.role} />
                    </div>
                    {(m.proposals ?? []).map((p, j) => (
                      <AssistantProposalCard key={p.id ?? `${i}-${j}`} proposal={p} />
                    ))}
                    {canSeeRecord && (m.patients ?? []).length > 0 ? (
                      <div className="asp__patients">
                        {m.patients!.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="asp__patientbtn"
                            onClick={() => setPeek(p)}
                          >
                            <FolderOpen size={14} aria-hidden="true" />
                            {t('peekVeziFisierele', { name: p.name })}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {ask.isPending ? (
                  <div className="asp__msg asp__msg--pending">{t('seGandeste')}</div>
                ) : null}
              </div>
            ) : (
              <ul className="asp__suggest">
                {suggestions.map((s) => (
                  <li key={s}>
                    <button type="button" onClick={() => send(s)} disabled={ask.isPending}>
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {error ? (
              <p className="asp__error" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <form className="asp__form" onSubmit={onSubmit}>
            <textarea
              ref={inputRef}
              aria-label={t('intrebareaPentruAsistent')}
              placeholder={
                reads.length ? t('intrebatiDespreAplicatieSau') : t('intrebatiCumSeFace')
              }
              rows={2}
              maxLength={2000}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
            />
            <div className="asp__formfoot">
              {mode === 'live' && limit.perDay !== null ? (
                <span className="asp__limit">
                  {t('nDinNAzi', { used: limit.usedToday, perDay: limit.perDay })}
                </span>
              ) : (
                <span />
              )}
              <button
                type="submit"
                className="asp__send"
                disabled={ask.isPending || draft.trim().length < 2}
                aria-label={t('intreaba')}
              >
                <Send size={15} aria-hidden />
              </button>
            </div>
          </form>
        </>
      )}
      {peek ? (
        <PatientPeek patientId={peek.id} name={peek.name} onClose={() => setPeek(null)} />
      ) : null}
    </aside>
  );
}

const CAP_ORDER: AppAssistantCapability['kind'][] = ['read', 'action', 'help'];

/**
 * Fereastra „?": tot ce poate face asistentul pentru rolul care întreabă,
 * grupat pe felul lucrului (citește, pregătește, ajută cu aplicația), cu
 * întrebări-exemplu pe care omul le poate trimite cu un clic. Lista vine de la
 * server, deja filtrată pe drepturile rolului — deci arată numai ce e permis.
 */
function AssistantCapabilities({
  capabilities,
  onClose,
  onPick,
}: {
  capabilities: AppAssistantCapability[];
  onClose: () => void;
  onPick: (question: string) => void;
}) {
  const { t } = useTranslation('common');
  const groupLabel: Record<AppAssistantCapability['kind'], string> = {
    read: t('capPoateCiti'),
    action: t('capPoatePregati'),
    help: t('capTeAjuta'),
  };
  const groups = CAP_ORDER.map((kind) => ({
    kind,
    items: capabilities.filter((c) => c.kind === kind),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="aspcap" role="dialog" aria-modal="true" aria-label={t('ceStieAsistentul')}>
      <div className="aspcap__panel">
        <header className="aspcap__head">
          <span className="aspcap__title">{t('ceStieAsistentul')}</span>
          <button
            type="button"
            className="aspcap__close"
            onClick={onClose}
            aria-label={t('inchide')}
          >
            <X size={16} />
          </button>
        </header>
        <p className="aspcap__intro">{t('capIntro')}</p>
        <div className="aspcap__body">
          {groups.map((g) => (
            <section key={g.kind} className="aspcap__group">
              <h3 className="aspcap__grouptitle">{groupLabel[g.kind]}</h3>
              {g.items.map((c) => (
                <div key={`${c.kind}-${c.label}`} className="aspcap__item">
                  <p className="aspcap__summary">{c.summary}</p>
                  {c.examples.length > 0 ? (
                    <ul className="aspcap__examples">
                      {c.examples.map((ex) => (
                        <li key={ex}>
                          <button type="button" onClick={() => onPick(ex)}>
                            {ex}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
